import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { TraceBoardItem } from './interfaces/TraceBoardItem';
import { useTraceBoardContext } from './TraceBoardContext';

export interface TraceBoardConnectorProps {
  rowIndex: number;
  columnIndex: number; // source column index
  sourceItem: TraceBoardItem;
  targetItem: TraceBoardItem;
}

const TraceBoardConnector: React.FC<TraceBoardConnectorProps> = ({
  rowIndex,
  columnIndex,
  sourceItem,
  targetItem
}) => {
  // const pathRef = useRef<SVGPathElement>(null!);
  const {
    selected,
    columnItemElements,
    subscribeItemElementChange,
    unsubscribeItemElementChange
  } = useTraceBoardContext();

  // get the source and target elements
  const sourceColumnItemElement = useMemo(
    () =>
      columnItemElements.find(
        c =>
          c.rowIndex === rowIndex &&
          c.columnIndex === columnIndex &&
          c.item.id === sourceItem.id
      ),
    [rowIndex, columnIndex, sourceItem.id, columnItemElements]
  );
  const targetColumnItemElement = useMemo(
    () =>
      columnItemElements.find(
        c =>
          c.rowIndex === rowIndex &&
          c.columnIndex === columnIndex + 1 &&
          c.item.id === targetItem.id
      ),
    [rowIndex, columnIndex, targetItem.id, columnItemElements]
  );

  // calculate connector edges and svg path
  const calculatePath = useCallback(() => {
    if (!sourceColumnItemElement || !targetColumnItemElement) {
      return '';
    }
    const sourceElement = sourceColumnItemElement.element;
    const targetElement = targetColumnItemElement.element;
    const boardRowElement = sourceElement.parentElement?.parentElement;
    if (!boardRowElement) return '';
    const boardRowRect = boardRowElement.getBoundingClientRect();
    const sourceRect = sourceElement.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();
    const sourceX = sourceRect.right - boardRowRect.left;
    const sourceY = sourceRect.top - boardRowRect.top + sourceRect.height / 2;
    const targetX = targetRect.left - boardRowRect.left;
    const targetY = targetRect.top - boardRowRect.top + targetRect.height / 2;
    const mid = (targetX - sourceX) / 2;
    const midX = sourceX + mid;
    let path = `M${sourceX} ${sourceY}`;
    path += ` C${midX + mid} ${sourceY}`;
    path += ` ${midX - mid} ${targetY}`;
    path += ` ${targetX} ${targetY}`;
    path += ` L${targetX} ${targetY}`;
    return path;
  }, [sourceColumnItemElement, targetColumnItemElement]);
  const [path, setPath] = useState(calculatePath());

  useEffect(() => {
    const onItemElementChange = () => {
      // pathRef.current.setAttribute('d', calculatePath());
      setPath(calculatePath());
    };
    subscribeItemElementChange(rowIndex, columnIndex, onItemElementChange);
    return () => {
      unsubscribeItemElementChange(rowIndex, columnIndex, onItemElementChange);
    };
  }, [
    columnIndex,
    rowIndex,
    calculatePath,
    subscribeItemElementChange,
    unsubscribeItemElementChange
  ]);

  // bold selected item's connectors
  const isSelected =
    (selected?.columnIndex === columnIndex &&
      selected?.itemId === sourceItem.id) ||
    (selected?.columnIndex === columnIndex + 1 &&
      selected?.itemId === targetItem.id);

  if (!path) return null;
  return (
    <g>
      <path
        d={path}
        // ref={pathRef}
        fill='none'
        stroke='#007aff'
        strokeWidth={isSelected ? 3 : 2}
        strokeDasharray={isSelected ? 0 : '4,4'}
      />
    </g>
  );
};

export default TraceBoardConnector;
